// ── Internationalisation (language registry) ─────────────────────────────────
// ADD A LANGUAGE in three small steps — nothing else in the app needs touching:
//   1. Register it in LANGS below (label / dir / latinDigits) and in LANG_ORDER.
//   2. Add its Persian→X translation map to TRANSLATIONS (e.g. `ar: APP_AR`), and
//      optionally fill each STRINGS[id].X (onboarding); anything missing falls
//      back to Persian.
//   3. Add its weekday names to DAY_NAMES in MiniApp.jsx (optional; falls back).
// `t(key)` reads the active language; Persian is the KEY itself, so fa never
// regresses and any untranslated string simply shows Persian.
import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { APP_EN } from "./strings-en.js";
import { C } from "./design-system.js";

// The one place that knows every language. Persian is RTL; add more as needed.
export const LANGS = {
  fa: { label: "فارسی", dir: "rtl", latinDigits: false },
  en: { label: "English", dir: "ltr", latinDigits: true },
};
// Persian-literal translation maps, per non-default language (fa needs none).
const TRANSLATIONS = { en: APP_EN };
// Order the toggle cycles through.
export const LANG_ORDER = ["fa", "en"];
const DEFAULT_LANG = "fa";

export const isLang = (l) => Object.prototype.hasOwnProperty.call(LANGS, l);
export const dirOf = (l) => LANGS[l]?.dir || "rtl";

// Persian ↔ Latin digits, driven by the registry (latinDigits flag).
const FA_DIGITS = "۰۱۲۳۴۵۶۷۸۹";
export function fmtNum(n, lang) {
  const s = String(n);
  return LANGS[lang]?.latinDigits ? s : s.replace(/\d/g, d => FA_DIGITS[d]);
}

// Client-side language guess (runs before login, so it can't hit the backend):
//   • a remembered manual choice always wins (URL ?lang, then localStorage)
//   • the Iranian messengers (Bale, Eitaa) → Persian
//   • Telegram → the user's app language, matched against registered languages
export function detectLang() {
  try {
    const q = new URLSearchParams(window.location.search).get("lang");
    if (isLang(q)) return q;
    const saved = localStorage.getItem("zoha_lang");
    if (isLang(saved)) return saved;
    const tg = window.Telegram?.WebApp;
    // Only Telegram's language_code is meaningful here. Identify the Iranian hosts
    // by what the HOST injects, not by the SDK globals: we self-host all three
    // non-Bale SDKs, so window.Eitaa.WebApp / window.Soroush.WebApp exist on every
    // platform and checking those would mark Telegram users as Iranian and force
    // them to Persian.
    // (Duplicated from platform.js hostKey() on purpose — importing it here would
    // create a cycle, since platform.js imports moduleLang from this module.)
    const isIranian = typeof window.EitaaWebviewProxy !== "undefined"
                   || typeof window.SoroushWebviewProxy !== "undefined"
                   || typeof window.ZohaAndroid !== "undefined"   // the Bazaar app
                   || !!window.Bale?.WebApp;
    const isTelegram = !!tg?.initData && !isIranian;
    const lc = (tg?.initDataUnsafe?.user?.language_code || "").toLowerCase();
    if (isTelegram && lc && !lc.startsWith("fa")) {
      const two = lc.slice(0, 2);
      return isLang(two) ? two : "en";   // known language, else English
    }
  } catch { /* ignore */ }
  return DEFAULT_LANG;
}

// Best-effort: tell the backend the user's language so bot DMs match. No-op until
// the user is logged in (no JWT yet during onboarding).
function persistLangToServer(lang) {
  try {
    const token = sessionStorage.getItem("jwt");
    if (!token) return;
    const base = import.meta.env.DEV ? "" : "";
    fetch(`${base}/users/language`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ lang }),
    }).catch(() => {});
  } catch { /* ignore */ }
}

export const STRINGS = {
  // ── Language toggle ──
  "lang.other":        { fa: "English", en: "فارسی" },

  // ── Onboarding ──
  "ob.loading":        { fa: "در حال آماده‌سازی…", en: "Preparing…" },
  "ob.skip":           { fa: "رد کردن", en: "Skip" },
  "ob.continue":       { fa: "ادامه", en: "Continue" },
  "ob.start":          { fa: "شروع کنیم ✨", en: "Let's begin ✨" },

  "ob.s1.title":       { fa: "کودکی، زودگذرتر از آن است که فکر می‌کنید",
                         en: "Childhood is more fleeting than you think" },
  "ob.s1.desc":        { fa: "هر شب فرصتی است که دیگر تکرار نمی‌شود. قصه‌ای که امشب می‌شنود، فردا بخشی از شخصیت اوست.",
                         en: "Every night is a chance that never returns. The story they hear tonight becomes part of who they are tomorrow." },

  "ob.s2.title":       { fa: "هر قصه، بذری که در جانِ کودک شما جوانه می‌زند",
                         en: "Every story, a seed that blossoms within your child" },
  "ob.s2.desc":        { fa: "ترس و خشم، کمرویی و حسادت، یا مهربانی و پشتکار — هر احساس و خصلتِ کودک می‌تواند در دلِ یک قصه پرورش یابد. او آنچه را نمی‌تواند به زبان بیاورد، در قصه می‌فهمد و آرام‌آرام رشد می‌کند.",
                         en: "Fear and anger, shyness and jealousy, or kindness and perseverance — every feeling and trait can be nurtured inside a story. What your child cannot put into words, they grasp in a story, and slowly grow." },

  "ob.s3.title":       { fa: "قصه‌ای که فقط برای فرزند شما ساخته شده",
                         en: "A story made for your child alone" },
  "ob.s3.desc":        { fa: "نه یک قصهٔ عمومی، بلکه قصه‌ای با نام او، متناسب با روحیه و نیازش — بر پایهٔ روان‌شناسی کودک، تا هر شب کمی شجاع‌تر شود.",
                         en: "Not a generic tale, but a story with their name, shaped to their spirit and needs — grounded in child psychology, so every night they grow a little braver." },

  "ob.s4.title":       { fa: "کار ما بر پایهٔ علم است",
                         en: "Our work is grounded in science" },
  "ob.s4.quote":       { fa: "وقتی داستان با مسائلی که ذهن کودک را به خود مشغول کرده پیوند دارد، به رشد او کمک می‌کند و از فشار درونی‌اش می‌کاهد.",
                         en: "When a story connects to the matters occupying a child's mind, it aids their growth and eases their inner tension." },
  "ob.s4.author":      { fa: "— برونو بتلهایم، روان‌شناس کودک",
                         en: "— Bruno Bettelheim, child psychologist" },
  "ob.s4.desc":        { fa: "داستان‌های ضحا قصه دقیقاً بر همین پایه ساخته می‌شوند: با ترس‌ها، علایق و روحیهٔ خاص فرزند شما — نه یک قصهٔ عمومی برای همه.",
                         en: "Zoha Ghese's stories are built on exactly this: around your child's own fears, interests, and spirit — not one generic tale for everyone." },
};

// Module-level mirror of the active language, for helpers that run OUTSIDE React
// components (number formatting, time-until strings, …). Kept in sync by the
// provider. `tt` is the non-hook translator with the same key rules as `t`.
let _moduleLang = "fa";
export function moduleLang() { return _moduleLang; }
function translate(key, lang) {
  const entry = STRINGS[key];
  if (entry) return entry[lang] ?? entry.fa ?? key;              // id-based (onboarding)
  return lang === "fa" ? key : (TRANSLATIONS[lang]?.[key] ?? key);  // Persian-literal
}
export function tt(key, vars) {
  let s = translate(key, _moduleLang);
  if (vars) for (const k in vars) s = s.split(`{${k}}`).join(vars[k]);
  return s;
}

const LangContext = createContext(null);

export function LangProvider({ children }) {
  const [lang, setLangState] = useState(detectLang);
  const dir = dirOf(lang);
  _moduleLang = lang;  // keep the out-of-React mirror current every render

  useEffect(() => {
    document.documentElement.dir = dir;
    document.documentElement.lang = lang;
  }, [dir, lang]);

  const setLang = useCallback((l) => {
    if (!isLang(l)) return;
    try { localStorage.setItem("zoha_lang", l); } catch { /* ignore */ }
    setLangState(l);
    persistLangToServer(l);
  }, []);

  const t = useCallback((key, vars) => {
    let s = translate(key, lang);
    if (vars) for (const k in vars) s = s.split(`{${k}}`).join(vars[k]);
    return s;
  }, [lang]);

  const num = useCallback((n) => fmtNum(n, lang), [lang]);

  return (
    <LangContext.Provider value={{ lang, setLang, dir, rtl: dir === "rtl", t, num }}>
      {children}
    </LangContext.Provider>
  );
}

// Safe fallback so components work even if rendered outside a provider.
const FALLBACK = {
  lang: "fa", dir: "rtl", rtl: true, setLang: () => {},
  t: (k) => translate(k, "fa"), num: (n) => fmtNum(n, "fa"),
};
export function useLang() {
  return useContext(LangContext) || FALLBACK;
}

// A compact pill that flips the language. Shows the OTHER language's name, so the
// label shows the NEXT language's own name, so it always reads "switch to X".
// Cycles through LANG_ORDER, so it keeps working when more languages are added.
export function LangToggle({ style, color = C.goldLight, bg = `${C.bg}8C` }) {
  const { lang, setLang } = useLang();
  const next = LANG_ORDER[(LANG_ORDER.indexOf(lang) + 1) % LANG_ORDER.length];
  return (
    <button
      onClick={() => setLang(next)}
      aria-label="Change language"
      style={{
        display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer",
        background: bg, border: `1px solid ${color}55`, color,
        borderRadius: 999, padding: "6px 13px", fontFamily: "inherit",
        fontSize: 13, fontWeight: 700, ...style,
      }}>
      <span style={{ fontSize: 13 }}>🌐</span>
      <span dir={dirOf(next)}>{LANGS[next].label}</span>
    </button>
  );
}
